import { createTheme } from '@mui/material/styles';

/**
 * The MUI theme, built from the brand.
 *
 * Every value here comes from the custom properties at the top of the PHP
 * theme's main.css, which in turn were taken from the logo. That matters:
 * dropped in unconfigured, MUI looks like Google — Roboto, blue, heavy
 * elevation shadows, SHOUTING BUTTON LABELS — and a tile contractor selling
 * hand-finished work would look like a phone settings screen.
 *
 * So the defaults are replaced rather than tweaked: the palette is gold on
 * charcoal, the face is Montserrat, the type scale is the same fluid clamp()
 * ramp the PHP theme uses, and elevation is dialled back to the two soft
 * shadows the design actually calls for.
 */

/* Brand colours, taken from the logo. */
export const brand = {
	dark: '#2d2d2d',
	darkSoft: '#3a3a3a',
	darkDeep: '#1f1f1f',
	accent: '#b68e4b',
	accentDark: '#9a7639',
	accentSoft: '#b19882',
	grey: '#94908d',
	greyLight: '#dfdbd8',
	white: '#fff',
	surface: '#faf9f8',
	text: '#2d2d2d',
	textMuted: '#6c6864',
	textInvert: '#f5f3f1',
	textInvertMuted: '#bdb8b4',
	line: 'rgb(45 45 45 / 12%)',
	lineLight: 'rgb(255 255 255 / 14%)',
};

/* Shadows the design uses. MUI's own 25-step elevation ramp is far heavier. */
export const shadows = {
	sm: '0 1px 3px rgb(45 45 45 / 8%)',
	card: '0 4px 20px rgb(45 45 45 / 8%)',
	lift: '0 14px 40px rgb(45 45 45 / 16%)',
	tile: '0 20px 50px rgb(31 31 31 / 30%)',
};

/* The fluid type scale. Each step grows with the viewport. */
const size = {
	'-1': 'clamp(0.78rem, 0.76rem + 0.1vw, 0.83rem)',
	0: 'clamp(0.95rem, 0.92rem + 0.15vw, 1.05rem)',
	1: 'clamp(1.1rem, 1.04rem + 0.3vw, 1.3rem)',
	2: 'clamp(1.35rem, 1.22rem + 0.6vw, 1.7rem)',
	3: 'clamp(1.65rem, 1.4rem + 1.1vw, 2.35rem)',
	4: 'clamp(2rem, 1.55rem + 2vw, 3.2rem)',
	5: 'clamp(2.4rem, 1.7rem + 3vw, 4rem)',
};

export const layout = {
	container: '75rem',
	containerNarrow: '46rem',
	headerHeight: '5.5rem',
	// Tablets and desktops get a taller bar: the logo mark filled the 5.5rem one
	// edge to edge, which read as cramped. Phones keep the compact bar.
	headerHeightWide: '6.5rem',
};

/*
 * The tile textures, carried over from the PHP theme's main.css.
 *
 * These are what make the site feel like a tile setter's rather than anyone's:
 * a faint grout-line grid (thin lines both ways, like tiled wall seen from
 * across the room) and a diagonal crosshatch of gold hairlines. Both are pure
 * CSS gradients — no image downloads, and cheap enough to run on any phone.
 *
 * Each takes the line colour so the same texture works on light and dark
 * grounds. The mask fades the pattern out so it reads as atmosphere, not
 * wallpaper.
 */
export const textures = {
	/** The grout grid. PHP theme: .hero::before. */
	groutGrid: (line = 'rgb(45 45 45 / 7%)', cell = '5rem') => ({
		backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
		backgroundSize: `${cell} ${cell}`,
	}),

	/** The diagonal crosshatch. PHP theme: .cta-band__pattern. */
	crosshatch: (a = brand.accent, b = brand.accentSoft) => ({
		backgroundImage: `repeating-linear-gradient(45deg, ${a} 0 1px, transparent 1px 3.5rem), repeating-linear-gradient(-45deg, ${b} 0 1px, transparent 1px 3.5rem)`,
	}),

	/** The warm bright hero ground. PHP theme: .hero. */
	heroGround: `radial-gradient(38rem 30rem at 82% 30%, rgb(182 142 75 / 14%), transparent 70%), linear-gradient(180deg, #fff 0%, ${brand.surface} 60%, #f1eeea 100%)`,
};

/** The web-ready logo files (viewBoxes tightened to the artwork). */
export const LOGO_DIR = '/wp-content/themes/top-tile-construction/assets/img/logos/web';

/** The full stacked lockup (366 x 475), used in the footer. */
export const LOGO_URL = `${LOGO_DIR}/primary-logo-trans.svg`;

const fontFamily =
	"'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

const theme = createTheme({
	cssVariables: true,

	palette: {
		mode: 'light',
		primary: {
			main: brand.accent,
			dark: brand.accentDark,
			light: brand.accentSoft,
			contrastText: brand.white,
		},
		secondary: {
			main: brand.dark,
			dark: brand.darkDeep,
			light: brand.darkSoft,
			contrastText: brand.textInvert,
		},
		text: {
			primary: brand.text,
			secondary: brand.textMuted,
		},
		background: {
			default: brand.white,
			paper: brand.white,
		},
		divider: brand.line,
	},

	typography: {
		fontFamily,
		// Headings are tight and heavy; body text is comfortable to read. The
		// contrast between the two is most of the site's character.
		h1: { fontFamily, fontSize: size[5], fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em' },
		h2: { fontFamily, fontSize: size[4], fontWeight: 700, lineHeight: 1.12, letterSpacing: '-0.015em' },
		h3: { fontFamily, fontSize: size[3], fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' },
		h4: { fontFamily, fontSize: size[2], fontWeight: 600, lineHeight: 1.3 },
		h5: { fontFamily, fontSize: size[1], fontWeight: 600, lineHeight: 1.4 },
		h6: { fontFamily, fontSize: size[0], fontWeight: 600, lineHeight: 1.5 },
		body1: { fontFamily, fontSize: size[0], lineHeight: 1.65 },
		body2: { fontFamily, fontSize: size['-1'], lineHeight: 1.6 },
		// Used for the small gold labels that sit above a section title.
		overline: {
			fontFamily,
			fontSize: size['-1'],
			fontWeight: 700,
			letterSpacing: '0.18em',
			textTransform: 'uppercase',
			lineHeight: 1.4,
		},
		button: {
			fontFamily,
			fontWeight: 600,
			letterSpacing: '0.02em',
			// Material shouts by default. Trades work sells on being straight with
			// people, so the buttons say "Get a free estimate", not "GET A FREE
			// ESTIMATE".
			textTransform: 'none',
		},
	},

	shape: { borderRadius: 8 },

	components: {
		MuiCssBaseline: {
			styleOverrides: `
				@font-face {
					font-family: 'Montserrat';
					font-style: normal;
					font-weight: 400;
					font-display: swap;
					src: url('/fonts/montserrat-v31-latin-regular.woff2') format('woff2');
				}
				@font-face {
					font-family: 'Montserrat';
					font-style: normal;
					font-weight: 500;
					font-display: swap;
					src: url('/fonts/montserrat-v31-latin-500.woff2') format('woff2');
				}
				@font-face {
					font-family: 'Montserrat';
					font-style: normal;
					font-weight: 600;
					font-display: swap;
					src: url('/fonts/montserrat-v31-latin-600.woff2') format('woff2');
				}
				@font-face {
					font-family: 'Montserrat';
					font-style: normal;
					font-weight: 700;
					font-display: swap;
					src: url('/fonts/montserrat-v31-latin-700.woff2') format('woff2');
				}

				html {
					-webkit-text-size-adjust: 100%;
					overflow-x: clip;
					scroll-padding-top: calc(${layout.headerHeightWide} + 1rem);
				}

				body {
					overflow-wrap: break-word;
					-webkit-font-smoothing: antialiased;
					/*
					 * The page never scrolls sideways. Anything that would push it
					 * wider than the screen — a nowrap line on a narrow phone, a
					 * grid track forced open by min-content — gets clipped instead
					 * of shoving the whole layout off-centre. The grids below also
					 * set min-width: 0 on their children so text wraps rather than
					 * being cut; this is the backstop, not the mechanism.
					 */
					overflow-x: clip;
				}

				/*
				 * Note what is absent: the PHP theme sets scroll-behavior: smooth,
				 * and this app deliberately does not. GSAP pins sections by taking
				 * over the scroll, and a browser animating the scroll position at
				 * the same time fights it. The anchor jumps on the services page
				 * use scrollIntoView({ behavior: 'smooth' }) in JS instead.
				 */

				:focus-visible {
					outline: 3px solid ${brand.accent};
					outline-offset: 3px;
				}
			`,
		},

		MuiButton: {
			defaultProps: {
				disableElevation: true,
			},
			styleOverrides: {
				root: {
					borderRadius: 8,
					paddingInline: '1.6rem',
					paddingBlock: '0.75rem',
					transition: 'transform 200ms ease, background-color 200ms ease, box-shadow 200ms ease',
					'&:hover': {
						transform: 'translateY(-2px)',
					},
					// Someone who asked for less motion should not get a button that
					// jumps at them either.
					'@media (prefers-reduced-motion: reduce)': {
						transition: 'background-color 200ms ease',
						'&:hover': { transform: 'none' },
					},
				},
				/*
				 * The sheen: a soft band of light sweeps across a solid button on
				 * hover. Ported line for line from the PHP theme's .btn--primary
				 * (main.css) — same gradient, same skew, same 550ms — because the
				 * owner liked that one specifically.
				 */
				contained: {
					position: 'relative',
					overflow: 'hidden',
					'&::before': {
						content: '""',
						position: 'absolute',
						top: 0,
						left: '-80%',
						width: '55%',
						height: '100%',
						background: 'linear-gradient(105deg, transparent, rgb(255 255 255 / 45%), transparent)',
						transform: 'skewX(-20deg)',
						transition: 'left 550ms ease',
						pointerEvents: 'none',
					},
					'&:hover::before, &:focus-visible::before': {
						left: '130%',
					},
					'@media (prefers-reduced-motion: reduce)': {
						'&::before': { display: 'none' },
					},
				},
				containedPrimary: {
					color: brand.white,
					boxShadow: shadows.sm,
					'&:hover': { backgroundColor: brand.accentDark, boxShadow: shadows.lift },
				},
				outlined: {
					borderWidth: 2,
					'&:hover': { borderWidth: 2 },
				},
			},
		},

		MuiPaper: {
			defaultProps: { elevation: 0 },
			styleOverrides: {
				root: {
					backgroundImage: 'none', // Kills MUI's overlay tint.
				},
			},
		},

		MuiCard: {
			defaultProps: { elevation: 0 },
			styleOverrides: {
				root: {
					border: `1px solid ${brand.line}`,
					borderRadius: 8,
					boxShadow: shadows.card,
				},
			},
		},

		MuiAppBar: {
			defaultProps: { elevation: 0, color: 'transparent' },
		},

		MuiContainer: {
			defaultProps: { maxWidth: false },
			styleOverrides: {
				root: {
					maxWidth: layout.container,
					marginInline: 'auto',
					paddingInline: '1.5rem',
				},
			},
		},

		MuiLink: {
			defaultProps: { underline: 'none' },
			styleOverrides: {
				root: {
					color: 'inherit',
					transition: 'color 200ms ease',
					'&:hover': { color: brand.accent },
				},
			},
		},

		MuiChip: {
			styleOverrides: {
				root: { fontWeight: 600, letterSpacing: '0.02em' },
			},
		},
	},
});

export default theme;
